import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { TileType, getLayout, type Layout } from '../../data/maze-data'
import { TILE_SIZE, WALL_HEIGHT } from '../../constants/game-config'
import { gridToWorld, worldWidth, worldDepth, MAZE_COLS } from '../../utils/grid'
import { buildThemeProp } from '../../procedural/models'
import { getLevelTheme, type LevelTheme } from '../../data/level-themes'
import { reefSurfaceCells } from './reef-topology'
import { reefGeometry, reefWallSections, type ReefAsset } from './reef-assets'
import { reefMaterials, applyReefLight, applyReefColony } from './reef-materials'

const PLAYER_WALKABLE = new Set<TileType>([TileType.Path, TileType.Dot, TileType.PowerPellet])
const ENEMY_WALKABLE = new Set<TileType>([
  TileType.Path,
  TileType.Dot,
  TileType.PowerPellet,
  TileType.Empty,
  TileType.Spawn,
])

/** Builds the static maze geometry for a level + theme, exposes the walkability graph +
 * tunnel wrap, and can REBUILD itself (new layout + theme) when the level changes. */
export class Maze {
  readonly group = new THREE.Group()
  readonly rows: number
  readonly cols: number
  tunnelRows = new Set<number>()

  private layout: Layout
  private theme: LevelTheme
  private wallMaterial: THREE.MeshStandardMaterial | null = null
  /** CPU-only section proxies for one-time, exact colony grounding. */
  private wallSurfaces = new Map<string, THREE.Mesh>()
  /** Geometries/materials owned by the current build — disposed on rebuild. */
  private owned: Array<{ dispose(): void }> = []

  constructor(level = 1, theme: LevelTheme = getLevelTheme(1)) {
    this.layout = getLayout(level)
    this.theme = theme
    this.rows = this.layout.length
    this.cols = this.layout[0].length
    this.build()
  }

  /** Swap to a new level's layout + theme, freeing the old geometry first. */
  rebuild(level: number, theme: LevelTheme): void {
    this.disposeBuild()
    this.layout = getLayout(level)
    this.theme = theme
    this.build()
  }

  /** Re-theme WITHOUT changing topology (e.g. a flash effect) — recolours walls only. */
  applyTheme(theme: LevelTheme): void {
    this.theme = theme
    this.recolorWalls()
  }

  private build(): void {
    this.detectTunnels()
    this.buildFloor()
    this.buildWalls()
    this.buildProps()
  }

  private disposeBuild(): void {
    this.group.clear()
    for (const o of this.owned) o.dispose()
    this.owned = []
    this.wallMaterial = null
    this.wallSurfaces.clear()
  }

  private track<T extends { dispose(): void }>(o: T): T {
    this.owned.push(o)
    return o
  }

  private detectTunnels(): void {
    this.tunnelRows = new Set<number>()
    for (let r = 0; r < this.rows; r++) {
      if (this.tileAt(r, 0) !== TileType.Wall && this.tileAt(r, this.cols - 1) !== TileType.Wall) {
        this.tunnelRows.add(r)
      }
    }
  }

  private tileAt(r: number, c: number): TileType {
    if (r < 0 || r >= this.rows || c < 0 || c >= this.cols) return TileType.Wall
    return this.layout[r][c]
  }

  private buildFloor(): void {
    const w = worldWidth() + TILE_SIZE * 28
    const d = worldDepth() + TILE_SIZE * 28
    // Clone the shared sand material so each level can tint its seabed independently.
    const floorMat = this.track(reefMaterials().sand.clone())
    floorMat.color.setHex(this.theme.floorColor).lerp(new THREE.Color(0xdde7df), 0.7)
    applyReefLight(floorMat, true)
    const floorGeo = this.track(new THREE.PlaneGeometry(w, d))
    const floor = new THREE.Mesh(floorGeo, floorMat)
    floor.rotation.x = -Math.PI / 2
    floor.position.y = -WALL_HEIGHT / 2 - 0.01
    floor.receiveShadow = true
    this.group.add(floor)
  }

  private buildWalls(): void {
    const parts: THREE.BufferGeometry[] = []
    const dummy = new THREE.Object3D()
    for (const section of reefWallSections(this.layout)) {
      const variant = (section.row * 13 + section.col * 7) % 2
      const source = reefGeometry(`wall-section-${section.length}-${variant}` as ReefAsset)
      if (!source) continue
      const col = section.col + (section.axis === 'x' ? (section.length - 1) / 2 : 0)
      const row = section.row + (section.axis === 'z' ? (section.length - 1) / 2 : 0)
      const { x, z } = gridToWorld(col, row)
      dummy.position.set(x, -WALL_HEIGHT / 2, z)
      dummy.rotation.set(0, section.axis === 'x' ? 0 : Math.PI / 2, 0)
      dummy.scale.set(1, 1.02 + Math.sin(row * 0.53 + col * 0.31) * 0.065, 1)
      dummy.updateMatrix()
      // Vary the actual crown within each run, keeping the horizontal footprint
      // unchanged. All grounding rays use this same sculpted geometry.
      const sculpted = this.track(source.clone())
      const vertices = sculpted.getAttribute('position')
      const normals = sculpted.getAttribute('normal')
      const normal = new THREE.Vector3()
      for (let i = 0; i < vertices.count; i++) {
        const px = vertices.getX(i), py = vertices.getY(i), pz = vertices.getZ(i)
        const crown = Math.min(1, Math.max(0, py / 0.95))
        const phaseA = px * 3.1 + row * 1.9 + col
        const phaseB = px * 1.8 + pz * 2.8 + col * 2.1
        const strata = Math.sin(phaseA) * 0.028 + Math.sin(phaseB) * 0.035
        vertices.setY(i, py + strata * crown * crown)
        // Carry the authored smooth normals through the crown deformation's
        // inverse transpose. Recomputing normals at UV splits gives each small
        // triangle a separate highlight, making weathered stone look shattered.
        if (normals) {
          const dx = (Math.cos(phaseA) * 0.028 * 3.1 + Math.cos(phaseB) * 0.035 * 1.8) * crown * crown
          const dz = Math.cos(phaseB) * 0.035 * 2.8 * crown * crown
          const dy = py > 0 && py < 0.95 ? strata * 2 * crown / 0.95 : 0
          const ny = normals.getY(i) / (1 + dy)
          normal.set(normals.getX(i) - dx * ny, ny, normals.getZ(i) - dz * ny).normalize()
          normals.setXYZ(i, normal.x, normal.y, normal.z)
        }
      }
      if (!normals) sculpted.computeVertexNormals()
      parts.push(sculpted.clone().applyMatrix4(dummy.matrix))
      const surface = new THREE.Mesh(sculpted, reefMaterials().limestone)
      surface.matrixAutoUpdate = false
      surface.matrix.copy(dummy.matrix)
      surface.updateMatrixWorld(true)
      for(let i=0;i<section.length;i++) {
        this.wallSurfaces.set(`${section.row+(section.axis==='z'?i:0)},${section.col+(section.axis==='x'?i:0)}`, surface)
      }
    }
    const geometry = parts.length ? mergeGeometries(parts, false) : null
    parts.forEach(part => part.dispose())
    if (!geometry) return
    const wallMaterial = this.track(reefMaterials().limestone.clone())
    applyReefLight(wallMaterial)
    this.wallMaterial = wallMaterial
    this.recolorWalls()
    const mesh = new THREE.Mesh(this.track(geometry), wallMaterial)
    mesh.name = 'ContinuousFracturedLimestoneSections'
    mesh.castShadow = true
    mesh.receiveShadow = true
    this.group.add(mesh)
  }

  /** Geological tint retains chapter identity without painting the reef neon. */
  private recolorWalls(): void {
    if (!this.wallMaterial) return
    const { hue, sat } = this.theme.wall
    this.wallMaterial.color.setHSL(hue, sat * 0.12, 0.79)
  }

  private buildProps(): void {
    const kit = this.theme.propKit
    const props: THREE.Object3D[] = []
    const groundingRay = new THREE.Raycaster(new THREE.Vector3(), new THREE.Vector3(0,-1,0))
    const support = (row: number, col: number, x: number, z: number): THREE.Intersection | undefined => {
      const surface = this.wallSurfaces.get(`${row},${col}`)
      if (!surface) return undefined
      groundingRay.ray.origin.set(x, 5, z)
      groundingRay.ray.direction.set(0, -1, 0)
      return groundingRay.intersectObject(surface, false)[0]
    }
    const reef = reefMaterials()
    const bankMaterial = this.track(reef.limestone.clone())
    bankMaterial.color.setHex(0xafbab1)
    applyReefLight(bankMaterial)
    const colonyMaterials = [0x6c5c87, 0x507f95, 0x8c9158, 0xa8583e].map(color => {
      const material = this.track(reef.coral.clone())
      material.color.setHex(color)
      applyReefColony(material)
      return material
    })
    const makeReefProp = (seed: number, kelp: boolean, encrusting = false): THREE.Object3D | undefined => {
      const geometry = reefGeometry(encrusting ? 'encrusting-coral-patch' : kelp ? 'kelp-ribbons' : 'coral-antlers')
      if (!geometry) return undefined
      const prop = new THREE.Mesh(geometry.clone(), kelp && !encrusting ? reef.kelp : colonyMaterials[seed % colonyMaterials.length])
      prop.rotation.y = seed * 2.399
      if (kelp && !encrusting) {
        const vertices = prop.geometry.getAttribute('position')
        const weights = new Float32Array(vertices.count)
        for (let i = 0; i < vertices.count; i++) weights[i] = Math.max(0, vertices.getY(i))
        prop.geometry.setAttribute('reefBend', new THREE.BufferAttribute(weights, 1))
        const width = 0.64 + (seed % 5) * 0.15
        prop.scale.set(width, 0.90 + (seed % 4) * 0.12, width)
      } else prop.scale.setScalar(encrusting ? 0.55 + (seed % 5) * 0.07 : 0.60 + (seed % 5) * 0.075)
      prop.castShadow = true
      prop.receiveShadow = true
      return prop
    }
    // Small real spalls break the broad section faces at the player's camera scale.
    // Every attachment is raycast against its own solid wall, and the resulting
    // geometry joins the static material batches; there are no per-frame objects.
    for (const cell of reefSurfaceCells(this.layout)) {
      if (!cell.edges.length) continue
      const { x, z } = gridToWorld(cell.col, cell.row)
      const seed = cell.row * 31 + cell.col * 7
      const surface = this.wallSurfaces.get(`${cell.row},${cell.col}`)
      if (!surface) continue
      for (let detail = 0; detail < 3; detail++) {
        const stone = reefGeometry(`fractured-stone-${(seed + detail) % 8}` as ReefAsset)
        if (!stone) continue
        let host: THREE.Intersection | undefined
        if (detail === 0) {
          host = support(cell.row, cell.col, x + Math.sin(seed + detail * 4.7) * 0.31, z + Math.cos(seed * 1.3 + detail) * 0.27)
        } else {
          const edge = cell.edges[(seed + detail) % cell.edges.length]
          const nx = edge === 'east' ? 1 : edge === 'west' ? -1 : 0
          const nz = edge === 'south' ? 1 : edge === 'north' ? -1 : 0
          const along = Math.sin(seed * 2.13 + detail * 4.3) * 0.32
          groundingRay.ray.origin.set(x + nx * 0.9 + nz * along,
            -WALL_HEIGHT / 2 + 0.26 + (detail - 1) * 0.38, z + nz * 0.9 + nx * along)
          groundingRay.ray.direction.set(-nx, 0, -nz)
          host = groundingRay.intersectObject(surface, false)[0]
        }
        if (!host?.face) continue
        const normal = host.face.normal.clone().applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(host.object.matrixWorld))
        const spall = new THREE.Mesh(stone.clone(), this.wallMaterial ?? bankMaterial)
        spall.position.copy(host.point).addScaledVector(normal, -0.035)
        spall.quaternion.setFromUnitVectors(THREE.Object3D.DEFAULT_UP, normal)
        spall.rotateY(seed * 2.399 + detail)
        spall.scale.set(0.20 + (seed % 4) * 0.035, detail === 0 ? 0.055 : 0.10, 0.17 + (seed % 3) * 0.035)
        spall.castShadow = true
        spall.receiveShadow = true
        props.push(spall)
      }
    }
    for (const cell of reefSurfaceCells(this.layout)) {
      // Only place accents at exposed reef margins; preserve corridor visibility.
      if (!cell.edges.length) continue
      const seed = cell.row * 31 + cell.col
      const hash = Math.sin(seed * 12.9898) * 43758.5453
      const habitat = Math.sin(cell.row * 0.73 + cell.col * 0.31) * Math.cos(cell.col * 0.63)
      const density = habitat > 0 ? 0.48 : 0.13
      if (hash - Math.floor(hash) > density) continue
      const prop = makeReefProp(seed, seed % 3 === 0 || kit === 'seaweed')
      if (!prop) continue
      const { x, z } = gridToWorld(cell.col, cell.row)
      const root = support(cell.row, cell.col, x, z)
      prop.position.set(x, (root?.point.y ?? -WALL_HEIGHT / 2 + 1.12) - 0.025, z)
      props.push(prop)
      const patchSource = reefGeometry('encrusting-coral-patch')
      if (patchSource && seed % 3 !== 0) {
        const patch = new THREE.Mesh(patchSource.clone(), colonyMaterials[(seed + 1) % colonyMaterials.length])
        const host = support(cell.row, cell.col, x + 0.15, z - 0.1)
        patch.position.set(x + 0.15, (host?.point.y ?? root?.point.y ?? -WALL_HEIGHT / 2 + 1.13) - 0.015, z - 0.1)
        if(host?.face) {
          const normal = host.face.normal.clone().applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(host.object.matrixWorld))
          patch.quaternion.setFromUnitVectors(THREE.Object3D.DEFAULT_UP, normal)
        }
        patch.rotateY(seed * 1.7)
        patch.scale.setScalar(0.58 + (seed % 3) * 0.12)
        patch.receiveShadow = true
        props.push(patch)
      }
    }
    // Authored colonies frame the outer reef, leaving the grid and tunnel exits unobstructed.
    for (let i = 0; i < 38; i++) {
      const edge = i % 2 === 0 ? -1 : 1
      const z = (i / 38 - 0.5) * (worldDepth() + 3)
      const prop = makeReefProp(i + 101, i % 3 !== 0, i % 2 === 0)
      if (!prop) continue
      prop.position.set(edge * (worldWidth() / 2 + 0.8 + (i % 4) * 0.45), -WALL_HEIGHT / 2, z)
      prop.scale.multiplyScalar(1.25 + (i % 4) * 0.2)
      props.push(prop)
    }
    // Banks consist of overlapping large eroded masses, with sand pockets between colonies.
    // The nearest edge is always beyond the collision grid and its tunnel landing cells.
    for (let i = 0; i < 168; i++) {
      const source = reefGeometry(`fractured-stone-${i % 8}` as ReefAsset)
      if (!source) continue
      const outcrop = new THREE.Mesh(source.clone(), bankMaterial)
      const side = i % 2 ? -1 : 1
      const cluster = i % 7
      const width = 0.6 + Math.abs(Math.sin(i * 2.17)) * 2.0
      const x = i < 122 ? side * (worldWidth() / 2 + 1.2 + width * 0.65 + cluster * 0.12) : ((i - 122) / 45 - 0.5) * (worldWidth() + 6)
      const z = i < 122 ? (Math.floor(i / 2) / 60 - 0.5) * (worldDepth() + 10) + Math.sin(i * 4.3) * 0.85 : side * (worldDepth() / 2 + 1.8 + width)
      outcrop.position.set(x, -WALL_HEIGHT / 2 - 0.1, z)
      outcrop.rotation.y = i * 2.399
      outcrop.scale.set(0.7 + (i % 5) * 0.30, 0.35 + (i % 7) * 0.12, 0.6 + (i % 4) * 0.36)
      outcrop.castShadow = true
      outcrop.receiveShadow = true
      props.push(outcrop)
      // Low growth follows real top and inward-facing rock surfaces instead of floating
      // above an estimated bounding height; two overlapping patches cover selected banks.
      if (i % 2 === 0) {
        const patchSource = reefGeometry('encrusting-coral-patch')
        outcrop.updateMatrixWorld(true)
        if (patchSource) for (let patchIndex=0;patchIndex<2;patchIndex++) {
          if (patchIndex === 0) {
            groundingRay.ray.origin.set(x + Math.sin(i)*outcrop.scale.x*.08, 5, z)
            groundingRay.ray.direction.set(0,-1,0)
          } else {
            const inward = new THREE.Vector3(-x,0,-z).normalize()
            groundingRay.ray.origin.copy(outcrop.position).addScaledVector(inward,3)
            groundingRay.ray.origin.y += outcrop.scale.y * .35
            groundingRay.ray.direction.copy(inward).negate()
          }
          const host = groundingRay.intersectObject(outcrop,false)[0]
          if(!host?.face) continue
          const normal = host.face.normal.clone().applyNormalMatrix(new THREE.Matrix3().getNormalMatrix(outcrop.matrixWorld))
          const patch = new THREE.Mesh(patchSource.clone(), colonyMaterials[(i+patchIndex)%colonyMaterials.length])
          patch.position.copy(host.point).addScaledVector(normal,.005)
          patch.quaternion.setFromUnitVectors(THREE.Object3D.DEFAULT_UP,normal)
          patch.rotateY(i*1.9+patchIndex)
          patch.scale.setScalar(Math.min(outcrop.scale.x,outcrop.scale.z)*0.72)
          patch.receiveShadow=true
          props.push(patch)
        }
      }
      if (i % 3 === 0) {
        const colony = makeReefProp(i * 17, i % 9 === 0, i % 6 === 0)
        if (colony) {
          outcrop.updateMatrixWorld(true)
          groundingRay.ray.origin.set(x, 5, z)
          groundingRay.ray.direction.set(0, -1, 0)
          const root = groundingRay.intersectObject(outcrop, false)[0]
          if (root) {
            colony.position.copy(root.point)
            colony.position.y -= 0.025
            colony.scale.multiplyScalar(i % 9 === 0 ? 1.15 : 1.45)
            props.push(colony)
          } else (colony as THREE.Mesh).geometry.dispose()
        }
      }
    }
    // A shelf of smaller stones continues under the outer colonies. The broad
    // size distribution prevents the perimeter reading as a necklace of boulders.
    for (let i = 0; i < 220; i++) {
      const source = reefGeometry(`fractured-stone-${i % 8}` as ReefAsset)
      if (!source) continue
      const side = i % 2 ? -1 : 1
      const stone = new THREE.Mesh(source.clone(), bankMaterial)
      const size = 0.09 + (Math.sin(i * 7.17) * 0.5 + 0.5) * 0.30
      stone.position.set(side * (worldWidth() / 2 + 0.68 + (i % 11) * 0.31),
        -WALL_HEIGHT / 2 - 0.015,
        (Math.floor(i / 2) / 109 - 0.5) * (worldDepth() + 6))
      stone.rotation.y = i * 2.399
      stone.scale.set(size * 1.4, size * 0.55, size)
      stone.receiveShadow = true
      stone.castShadow = true
      props.push(stone)
    }
    // Keep each later chapter's established semantic prop family alongside its living reef.
    if (kit !== 'reef' && kit !== 'seaweed') {
      for (let i = 0; i < 8; i++) {
        const prop = buildThemeProp(kit, this.theme.level * 131 + i, this.theme.accent, true)
        prop.position.set((i % 2 ? -1 : 1) * (worldWidth() / 2 + 1.4), -WALL_HEIGHT / 2, (i / 8 - 0.5) * worldDepth())
        props.push(prop)
      }
    }
    this.mergePropsByMaterial(props)
    this.wallSurfaces.clear()
  }

  /** Bake static colony transforms and merge by shared material. Source geometries here
   * are level-owned clones; the GLB cache remains untouched across level rebuilds. */
  private mergePropsByMaterial(props: THREE.Object3D[]): void {
    interface Bucket {
      geos: THREE.BufferGeometry[]
      cast: boolean
      receive: boolean
    }
    const buckets = new Map<THREE.Material, Bucket>()
    for (const prop of props) {
      prop.updateMatrixWorld(true)
      prop.traverse((o) => {
        const mesh = o as THREE.Mesh
        if (!mesh.isMesh || !mesh.geometry) return
        if (Array.isArray(mesh.material)) {
          // Multi-material meshes can't be bucketed by a single Material — they'd be SILENTLY dropped
          // from the world (and leak). No prop factory uses array materials today; warn loudly in DEV
          // so a future one that does is caught instead of vanishing.
          if (import.meta.env.DEV) console.warn('[Maze] prop mesh with array material skipped from merge — it will not appear:', mesh.name || mesh.uuid)
          mesh.geometry.dispose() // don't leak the skipped source geometry's GPU buffers
          return
        }
        const baked = mesh.geometry.clone()
        baked.applyMatrix4(mesh.matrixWorld) // bake world transform (props never move individually)
        let b = buckets.get(mesh.material as THREE.Material)
        if (!b) {
          b = { geos: [], cast: mesh.castShadow, receive: mesh.receiveShadow }
          buckets.set(mesh.material as THREE.Material, b)
        }
        b.geos.push(baked)
        mesh.geometry.dispose() // the source geometry is cloned into the bucket; free it now
      })
    }
    for (const [mat, b] of buckets) {
      const merged = mergeGeometries(b.geos, false)
      if (merged) {
        b.geos.forEach((g) => g.dispose()) // copied into `merged`
        const mesh = new THREE.Mesh(merged, mat)
        mesh.castShadow = b.cast
        mesh.receiveShadow = b.receive
        this.owned.push(merged)
        this.group.add(mesh)
      } else {
        // Attribute mismatch within a material (e.g. mixed indexed/non-indexed) — fall back to
        // adding the baked geometries individually so nothing disappears. Still correct, not batched.
        for (const g of b.geos) {
          const mesh = new THREE.Mesh(g, mat)
          mesh.castShadow = b.cast
          mesh.receiveShadow = b.receive
          this.owned.push(g)
          this.group.add(mesh)
        }
      }
    }
  }

  /** Cheap deterministic hash of the current layout — lets tests assert levels differ. */
  signature(): number {
    let h = 2166136261
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        h = (h ^ (this.layout[r][c] * (r * 31 + c + 1))) >>> 0
        h = (h * 16777619) >>> 0
      }
    }
    return h
  }

  inBounds(r: number, c: number): boolean {
    return r >= 0 && r < this.rows && c >= 0 && c < this.cols
  }

  isWalkable(r: number, c: number, forEnemy = false): boolean {
    if (!this.inBounds(r, c)) return false
    const tile = this.layout[r][c]
    return (forEnemy ? ENEMY_WALKABLE : PLAYER_WALKABLE).has(tile)
  }

  /** Apply horizontal tunnel wrap to a column on a tunnel row. */
  wrapCol(row: number, col: number): number {
    if (!this.tunnelRows.has(row)) return col
    if (col < 0) return this.cols - 1
    if (col >= this.cols) return 0
    return col
  }

  isTunnelRow(row: number): boolean {
    return this.tunnelRows.has(row)
  }

  /** All pellet tiles for the collectible spawner. */
  pelletTiles(): Array<{ row: number; col: number; type: TileType }> {
    const out: Array<{ row: number; col: number; type: TileType }> = []
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const t = this.layout[r][c]
        if (t === TileType.Dot || t === TileType.PowerPellet) out.push({ row: r, col: c, type: t })
      }
    }
    return out
  }
}

export { MAZE_COLS }
